import { createBigDecimal, plus, times } from '@0xtorch/big-decimal'
import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { getAsset } from '../../utils/getAsset'
import { bitgetApiEndpoint, bitgetSymbolCryptoIdMap } from '../constants'
import { createPrivateGetHeaders } from '../utils'
import { marginAssetSchema } from './schema'

const responseSchema = z.object({
  code: z.string(),
  msg: z.string(),
  requestTime: z.number().int(),
  data: z.array(marginAssetSchema),
})

const urlPath = '/api/v2/margin/crossed/account/assets'

type FetchCrossMarginAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  password: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchCrossMarginAccountAssets = async ({
  apiKey,
  apiSecret,
  password,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchCrossMarginAccountAssetsParameters): Promise<
  readonly AssetBalance[]
> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, bitgetApiEndpoint)
  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          ...proxyHeaders,
          ...(await createPrivateGetHeaders({
            apiKey,
            apiSecret,
            password,
            urlPath: url.pathname,
            query: '',
          })),
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response.data) {
    const asset = getAsset({
      symbol: assetData.coin,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: bitgetSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    const cryptoId = asset.type === 'CryptoCurrency' ? asset.id : undefined
    const fiatId = asset.type === 'FiatCurrency' ? asset.id : undefined
    const balance = plus(assetData.available, assetData.frozen)
    if (balance.value > 0n) {
      assetBalances.push({
        key: 'bitget_cross-margin-balance',
        cryptoId,
        fiatId,
        amount: balance,
        timestamp,
      })
    }
    const borrow = times(
      plus(assetData.borrow, assetData.interest),
      createBigDecimal(-1n),
    )
    if (borrow.value !== 0n) {
      assetBalances.push({
        key: 'bitget_cross-margin-borrow',
        cryptoId,
        fiatId,
        amount: borrow,
        timestamp,
      })
    }
  }

  return assetBalances
}
